import { useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { ChevronDownIcon } from 'lucide-react';

import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    useComboboxAnchor,
} from '@talentmesh/ui/components/combobox';
import { Field, FieldError, FieldLabel } from '@talentmesh/ui/components/field';
import { cn } from '@talentmesh/ui/lib/utils';

export type RHFComboboxOption = { id: string; label?: string; name?: string };

function defaultGetOptionLabel(option: RHFComboboxOption): string {
    if (option.label) return option.label;
    if (option.name) return option.name;
    return option.id;
}

export type RHFComboboxProps<T extends RHFComboboxOption = RHFComboboxOption> = {
    id?: string | undefined;
    name: string;
    label: string;
    options: T[];
    disabled?: boolean;
    fieldDataCy?: string;
    hideFieldError?: boolean;
    getOptionLabel?: (option: T) => string;
    emptyValue?: T;
};

function RHFCombobox<T extends RHFComboboxOption>({
    id,
    name,
    label,
    options,
    disabled,
    fieldDataCy,
    hideFieldError,
    getOptionLabel = defaultGetOptionLabel as (option: T) => string,
    emptyValue,
}: RHFComboboxProps<T>) {
    const anchorRef = useComboboxAnchor();
    const { field, fieldState } = useController({ name });
    const error = fieldState.error;
    const selected = field.value as T | null | undefined;
    const hasSelection = Boolean(selected && String(selected.id).length > 0);
    const [inputValue, setInputValue] = useState(() => (hasSelection && selected ? getOptionLabel(selected) : ''));
    const [filterValue, setFilterValue] = useState('');
    const comboBoxId = id ?? name;

    useEffect(() => {
        if (hasSelection && selected) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInputValue(getOptionLabel(selected));
        } else {
            setInputValue('');
        }
    }, [hasSelection, selected, getOptionLabel]);

    const filteredOptions = useMemo(() => {
        const q = filterValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(q));
    }, [options, filterValue, getOptionLabel]);

    const cleared: T = emptyValue ?? ({ id: '', label: '' } as T);
    const comboboxValue: T | null = hasSelection && selected ? selected : null;
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (open) {
            setFilterValue('');
        }
    };

    const errorObj = error as Record<string, { message?: string } | undefined> | undefined;
    const errorMessage = error?.message
        ? String(error.message)
        : errorObj?.id?.message
          ? String(errorObj.id.message)
          : errorObj?.label?.message
            ? String(errorObj.label.message)
            : null;

    return (
        <Field className="w-full" data-invalid={error ? true : undefined} data-cy={fieldDataCy}>
            <FieldLabel htmlFor={comboBoxId}>{label}</FieldLabel>
            <div ref={anchorRef} className="w-full">
                <Combobox<T | null>
                    value={comboboxValue}
                    onValueChange={(next) => {
                        const value = (next ?? cleared) as T;
                        field.onChange(value);
                        if (next && String((next as T).id).length > 0) {
                            setInputValue(getOptionLabel(next as T));
                        } else {
                            setInputValue('');
                        }
                        setFilterValue('');
                        setIsOpen(false);
                    }}
                    inputValue={inputValue}
                    onInputValueChange={(v) => {
                        setInputValue(v);
                        setFilterValue(v);
                    }}
                    open={isOpen}
                    onOpenChange={handleOpenChange}
                    disabled={disabled}
                    isItemEqualToValue={(a, b) =>
                        Boolean(a && b && typeof a === 'object' && typeof b === 'object' && (a as T).id === (b as T).id)
                    }
                    itemToStringLabel={(v) => getOptionLabel(v as T)}
                >
                    <ComboboxInput
                        id={comboBoxId}
                        className={cn(
                            'ring-offset-background bg-card h-10 min-h-10 w-full max-w-full shadow-none transition-colors duration-200',
                            'has-[[data-slot=input-group-control]:focus-visible]:border-primary has-[[data-slot=input-group-control]:focus-visible]:ring-primary has-[[data-slot=input-group-control]:focus-visible]:ring-1 has-[[data-slot=input-group-control]:focus-visible]:ring-offset-0',
                            error &&
                                'has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:ring-destructive dark:has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive/50 dark:has-[[data-slot=input-group-control][aria-invalid=true]]:ring-destructive/50',
                        )}
                        disabled={disabled}
                        showTrigger={false}
                        showClear={false}
                        onBlur={field.onBlur}
                        name={field.name}
                        aria-invalid={error ? true : undefined}
                    >
                        <ChevronDownIcon className="pointer-events-none mr-2 size-4 text-inherit opacity-50" />
                    </ComboboxInput>
                    <ComboboxContent anchor={anchorRef}>
                        <ComboboxList>
                            {filteredOptions.map((option) => (
                                <ComboboxItem key={option.id} value={option}>
                                    {getOptionLabel(option)}
                                </ComboboxItem>
                            ))}
                        </ComboboxList>
                        {filteredOptions.length === 0 && <ComboboxEmpty>No matches</ComboboxEmpty>}
                    </ComboboxContent>
                </Combobox>
            </div>
            {!hideFieldError && errorMessage && <FieldError>{errorMessage}</FieldError>}
        </Field>
    );
}

export { RHFCombobox };
